import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PropTypes } from 'prop-types';
import ReactQuill from 'react-quill';
import Joi from 'joi';

import {
    useCloneInvestmentFundTunnelMutation,
    useGetInvestmentFundsQuery,
    useGetTunnelsQuery,
    useUpdateCustomTunnelStepMutation,
} from 'api/main';
import { fundTypes, quillFormats, stepIDs } from 'utils/constants';
import { validateSchema } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import TunnelPreview from './TunnelPreview';
import {
    ActionButton,
    CustomFormLabel,
    CustomSelect,
    CustomTextField,
    ErrorMessage,
    Loader,
    SuccessMessage,
} from 'components';
import { Box, MenuItem } from '@mui/material';

const schema = {
    sourceFundId: Joi.number().required().messages({ 'number.base': texts.SAISIR_LE_CHAMP }),
    surveyStepTitle: Joi.string()
        .trim()
        .required()
        .messages({ 'string.empty': texts.SAISIR_LE_CHAMP }),
};

const TunnelCloning = ({
    fundId,
    fundType,
    pendingTunnels,
    excludedStepIds,
    ppTunnelSteps,
    pmTunnelSteps,
}) => {
    const { data: funds = [], isLoading: fundsLoader } = useGetInvestmentFundsQuery();
    const [form, setForm] = useState({
        sourceFundId: '',
        surveyStepTitle: '',
        surveyStepDescription: '',
    });
    const [errors, setErrors] = useState({
        sourceFundId: '',
        surveyStepTitle: '',
        surveyStepDescription: '',
    });

    const { data: defaultTunnels = [], isLoading: defaultTunnelsLoader } = useGetTunnelsQuery(
        { investmentFunds: form.sourceFundId, status: 'default_active' },
        { skip: !form.sourceFundId }
    );
    const { data: activeTunnels, isLoading: activeTunnelsLoader } = useGetTunnelsQuery(
        { investmentFunds: form.sourceFundId, status: 'active' },
        { skip: !form.sourceFundId }
    );
    const [cloneInvestmentFund, { isLoading: cloneLoading, isSuccess: cloneSuccess }] =
        useCloneInvestmentFundTunnelMutation({
            fixedCacheKey: 'tunnel-cloning',
        });
    const [updateCustomTunnelStep, { isLoading: updateLoading, isSuccess: updateSuccess }] =
        useUpdateCustomTunnelStepMutation();

    const currentTunnels = useMemo(
        () => (activeTunnels?.length ? activeTunnels : defaultTunnels),
        [activeTunnels, defaultTunnels]
    );
    const pendingPPTunnel = useMemo(
        () => pendingTunnels?.find(t => t.name.includes('pp')),
        [pendingTunnels]
    );
    const pendingPMTunnel = useMemo(
        () => pendingTunnels?.find(t => t.name.includes('pm')),
        [pendingTunnels]
    );

    const ppTemplateTunnelSteps = useMemo(
        () =>
            currentTunnels
                .find(t => t.name.includes('pp'))
                ?.tunnelPhases.reduce((acc, ph) => {
                    acc = acc.concat(ph.tunnelSteps.map(ts => ({ ...ts, tunnelPhaseId: ph.id })));
                    return acc;
                }, [])
                ?.filter(ts => !excludedStepIds.includes(ts.step.id)),
        [excludedStepIds, currentTunnels]
    );

    const pmTemplateTunnelSteps = useMemo(
        () =>
            currentTunnels
                .find(t => t.name.includes('pm'))
                ?.tunnelPhases.reduce((acc, ph) => {
                    acc = acc.concat(ph.tunnelSteps.map(ts => ({ ...ts, tunnelPhaseId: ph.id })));
                    return acc;
                }, [])
                ?.filter(ts => !excludedStepIds.includes(ts.step.id)),
        [excludedStepIds, currentTunnels]
    );

    const ppPendingTunnelSteps = useMemo(
        () =>
            pendingPPTunnel
                ? pendingPPTunnel.tunnelPhases
                      .reduce((acc, ph) => {
                          acc = acc.concat(
                              ph.tunnelSteps.map(ts => ({ ...ts, tunnelPhaseId: ph.id }))
                          );
                          return acc;
                      }, [])
                      .filter(ts => !excludedStepIds.includes(ts.step.id))
                : [],
        [excludedStepIds, pendingPPTunnel]
    );

    const previewPPTunnelSteps = useMemo(
        () => (pendingPPTunnel ? ppTunnelSteps : ppTemplateTunnelSteps),
        [pendingPPTunnel, ppTunnelSteps, ppTemplateTunnelSteps]
    );

    const previewPMTunnelSteps = useMemo(
        () => (pendingPMTunnel ? pmTunnelSteps : pmTemplateTunnelSteps),
        [pendingPMTunnel, pmTunnelSteps, pmTemplateTunnelSteps]
    );

    const ppSurveyResultsStep = useMemo(
        () => previewPPTunnelSteps?.find(ts => ts.step.id === stepIDs.SURVEY_RESULTS),
        [previewPPTunnelSteps]
    );
    const pmSurveyResultsStep = useMemo(
        () => previewPMTunnelSteps?.find(ts => ts.step.id === stepIDs.SURVEY_RESULTS),
        [previewPMTunnelSteps]
    );

    const handleChange = useCallback(
        key => e => {
            setForm(prevState => ({
                ...prevState,
                // clear the other fields if the fund is changed; their values will be updated accordingly in a side effect
                surveyStepTitle: key === 'sourceFundId' ? '' : prevState.surveyStepTitle,
                surveyStepDescription:
                    key === 'sourceFundId' ? '' : prevState.surveyStepDescription,
                [key]: e.target.value + '',
            }));
        },
        []
    );

    const handleChangeDescription = useCallback(newValue => {
        setForm(prevState => ({ ...prevState, surveyStepDescription: newValue }));
    }, []);

    const handleSave = useCallback(() => {
        const { valid, newErrors } = validateSchema(schema, form);
        if (valid) {
            setErrors({ sourceFundId: '', surveyStepTitle: '', surveyStepDescription: '' });
            if (pendingPPTunnel) {
                // the tunnel is already cloned so we only update the "Survey Results" step
                updateCustomTunnelStep({
                    ppId: ppSurveyResultsStep.id,
                    pmId: pmSurveyResultsStep.id,
                    title: form.surveyStepTitle,
                    description:
                        form.surveyStepDescription === '<p><br></p>'
                            ? ''
                            : form.surveyStepDescription,
                });
            } else {
                // this only happens when there's no pending tunnel already cloned
                cloneInvestmentFund({ id: fundId, ...form, sourceFundId: form.sourceFundId });
            }
        } else {
            setErrors(newErrors);
        }
    }, [
        form,
        pendingPPTunnel,
        updateCustomTunnelStep,
        ppSurveyResultsStep,
        pmSurveyResultsStep,
        cloneInvestmentFund,
        fundId,
    ]);

    useEffect(() => {
        if ((pendingTunnels?.length && ppPendingTunnelSteps?.length) || activeTunnels?.length) {
            let cloningSource = pendingTunnels?.length
                ? pendingTunnels[0].cloningSource.investmentFund
                : activeTunnels[0].cloningSource.investmentFund;
            // update form according to the selected fund tunnel data
            setForm(prevState => ({
                sourceFundId: prevState.sourceFundId || cloningSource,
                surveyStepTitle: ppSurveyResultsStep?.title || '',
                surveyStepDescription: ppSurveyResultsStep?.description || '',
            }));
        }
    }, [
        ppSurveyResultsStep?.id,
        ppSurveyResultsStep?.title,
        ppSurveyResultsStep?.description,
        pendingTunnels,
        activeTunnels,
        ppPendingTunnelSteps?.length,
    ]);

    return (
        <Loader loading={fundsLoader || defaultTunnelsLoader || activeTunnelsLoader} marginTop={10}>
            <CustomSelect
                label={texts.TUNNELS}
                value={form.sourceFundId}
                onChange={handleChange('sourceFundId')}
                error={errors.sourceFundId}
                helperText={errors.sourceFundId}
                disabled={!!pendingPPTunnel}
            >
                {funds
                    .filter(f => f.type === fundType)
                    .map(f => (
                        <MenuItem key={f.id} value={f.id}>
                            {f.name}
                        </MenuItem>
                    ))}
            </CustomSelect>
            <CustomTextField
                label={texts.RESULTS_STEP_TITLE}
                value={form.surveyStepTitle}
                onChange={handleChange('surveyStepTitle')}
                error={errors.surveyStepTitle}
            />
            <CustomFormLabel>{texts.RESULTS_STEP_SUBTITLE}</CustomFormLabel>
            <ReactQuill
                className="small"
                value={form.surveyStepDescription}
                onChange={handleChangeDescription}
                formats={quillFormats}
            />
            {!!(previewPPTunnelSteps?.length && previewPMTunnelSteps?.length) && (
                <TunnelPreview
                    previewOnly
                    isFCPR={fundType === fundTypes.FCPR}
                    surveyStepTitle={form.surveyStepTitle}
                    ppTunnelSteps={previewPPTunnelSteps}
                    pmTunnelSteps={previewPMTunnelSteps}
                />
            )}
            <ErrorMessage message={errors.surveyStepDescription} />
            <SuccessMessage message={cloneSuccess || updateSuccess ? texts.DATA_SAVED : null} />
            <Box
                display="flex"
                justifyContent="center"
                flexDirection="column"
                alignItems="center"
                gridColumn="1 / span 2"
                gap={1}
            >
                <ActionButton
                    disabled={!form.sourceFundId && !form.surveyStepTitle.trim()}
                    variant="contained"
                    onClick={handleSave}
                    loading={cloneLoading || updateLoading}
                >
                    {texts.SAVE}
                </ActionButton>
            </Box>
        </Loader>
    );
};

TunnelCloning.propTypes = {
    fundId: PropTypes.number.isRequired,
    fundType: PropTypes.string,
    pendingTunnels: PropTypes.array,
    excludedStepIds: PropTypes.array,
    ppTunnelSteps: PropTypes.array,
    pmTunnelSteps: PropTypes.array,
};

export default TunnelCloning;
